import { useEffect } from "react";
import { useThemeStore, type ThemePreset } from "@pos/shared/stores";

export interface ThemePresetMeta {
  id: ThemePreset;
  label: string;
  // Swatch color shown in the Settings preset picker. Mirrors the
  // --primary value the preset applies. Hex over OKLCH so the swatch
  // renders identically without needing the CSS pipeline.
  swatch: string;
}

export const THEME_PRESETS: ThemePresetMeta[] = [
  { id: "red", label: "Red", swatch: "#ef485d" },
  { id: "blue", label: "Blue", swatch: "#3b82f6" },
  { id: "green", label: "Green", swatch: "#10b981" },
  { id: "violet", label: "Violet", swatch: "#8b5cf6" },
  { id: "slate", label: "Slate", swatch: "#475569" },
];

/**
 * Mounts the theme store onto the document. Toggles the `dark` class
 * on <html> for light/dark mode (with system preference tracking) and
 * sets `data-theme` for the brand color preset. Called once at the app
 * root; further changes flow automatically through the Zustand
 * subscriptions.
 */
export function useApplyTheme(): void {
  const mode = useThemeStore((s) => s.mode);
  const preset = useThemeStore((s) => s.preset);

  // Color preset — `red` is the :root default, so we clear the
  // attribute for it instead of writing a redundant value.
  useEffect(() => {
    const root = document.documentElement;
    if (preset === "red") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", preset);
    }
  }, [preset]);

  // Light / dark mode. `system` follows the OS appearance live —
  // we listen to the `prefers-color-scheme` media query so the UI
  // flips when the user changes their OS theme without restarting.
  useEffect(() => {
    const root = document.documentElement;

    function apply(): void {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      const isDark = mode === "dark" || (mode === "system" && prefersDark);
      root.classList.toggle("dark", isDark);
    }

    apply();

    if (mode !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [mode]);
}
